"use client";
import { CMSLink } from "@/Link";
import type { ResolvedCMSLink } from "@/blocks/resolve";
import { ArrowRight } from "lucide-react";
import Image from "next/image";
import RichText from "@/components/RichText";
import {
	Carousel,
	CarouselContent,
	CarouselItem,
} from "@/components/ui/carousel";
import type { DefaultTypedEditorState } from "@payloadcms/richtext-lexical";
import { cn } from "@/lib/utils";
import type { MediaObjectFit } from "@/utilities/mediaObjectFit";
import { mediaObjectFitClassName } from "@/utilities/mediaObjectFit";

interface OneTwoThreeProps {
	sectionId?: string;
	heroTitle?: string;
	heroSubtitle?: string;
	heroTitleTag?: "h1" | "h2" | "h3";
	heroTitleClassName?: string;
	sectionBackgroundImageSrc?: string;
	sectionBackgroundImageAlt?: string;
	sectionBackgroundImageTitle?: string;
	sectionBackgroundImageObjectFit?: MediaObjectFit;
	cards: {
		title?: string;
		subtitle?: string;
		body?: DefaultTypedEditorState;
		links?: ResolvedCMSLink[];
		imageSrc?: string;
		imageAlt?: string;
		imageTitle?: string;
		imageObjectFit?: MediaObjectFit;
	}[];
}

export function OneTwoThree({
	sectionId,
	heroTitle,
	heroSubtitle,
	heroTitleTag = "h2",
	heroTitleClassName = "heading-lg",
	sectionBackgroundImageSrc,
	sectionBackgroundImageAlt,
	sectionBackgroundImageTitle,
	sectionBackgroundImageObjectFit = "cover",
	cards,
}: OneTwoThreeProps) {
	const HeroTitleTag = heroTitleTag;
	const mobileHasPeekCard = cards.length > 1;
	const largeHasPeekCard = cards.length > 2;

	const renderCard = (
		card: OneTwoThreeProps["cards"][number],
		index: number,
	) => (
		<div
			key={`${card.title ?? "card"}-${index}`}
			className="flex h-full w-full flex-col overflow-hidden rounded-xs bg-[#f1f0ee] lg:rounded-md"
		>
			{card.imageSrc ? (
				<div className="relative aspect-16/6 w-full">
					<Image
						src={card.imageSrc}
						alt={card.imageAlt ?? ""}
						title={card.imageTitle ?? card.imageAlt ?? undefined}
						fill
						aria-hidden={card.imageAlt ? undefined : true}
						className={mediaObjectFitClassName(card.imageObjectFit)}
					/>
				</div>
			) : null}

			<div className="flex flex-1 flex-col px-5 py-6 md:p-8">
				<div className="flex h-full flex-col justify-between gap-8">
					<div className="flex flex-col">
						<div className="flex flex-col gap-2 pb-6:last">
							{card.title || card.subtitle ? (
								<div className="flex flex-col">
									{card.title ? (
										<h3 className="text-xl text-foreground text-pretty">
											{card.title}
										</h3>
									) : null}
									{card.subtitle ? (
										<p className="text-muted-foreground text-xl text-pretty whitespace-pre-line">
											{card.subtitle}
										</p>
									) : null}
								</div>
							) : null}
							{card.body ? (
								<RichText
									data={card.body}
									enableGutter={false}
									enableProse={false}
									className="text-muted-foreground text-base text-pretty"
								/>
							) : null}
						</div>
					</div>

					{card.links?.length ? (
						<div className="flex flex-col items-start gap-3">
							{card.links.map((link, linkIndex) => (
								<CMSLink
									key={`${link.label ?? "link"}-${linkIndex}`}
									{...link}
									appearance={link.appearance ?? "default"}
									size="lg"
									className="group"
								>
									<ArrowRight className="transition-transform duration-200 group-hover:translate-x-1 group-hover/button:translate-x-1" />
								</CMSLink>
							))}
						</div>
					) : null}
				</div>
			</div>
		</div>
	);

	return (
		<section id={sectionId} className="bg-background section-padding-y">
			<div className="container mx-auto container-padding-x">
				{heroTitle || heroSubtitle ? (
					<div className="section-title-gap-xl mb-10 max-w-4xl flex flex-col items-start">
						<div className="flex flex-col gap-1 md:gap-2">
							{heroTitle ? (
								<HeroTitleTag
									className={cn(
										heroTitleClassName,
										"text-foreground w-full text-balance",
									)}
								>
									{heroTitle}
								</HeroTitleTag>
							) : null}
							{heroSubtitle ? (
								<p
									className={cn(
										heroTitleClassName,
										"text-muted-foreground w-full",
									)}
								>
									{heroSubtitle}
								</p>
							) : null}
						</div>
					</div>
				) : null}

				<div
					className={cn(
						"relative overflow-hidden max-sm:mx-0 md:-mx-6",
						sectionBackgroundImageSrc ? "py-12 rounded-xs md:rounded-md" : "",
					)}
				>
					{sectionBackgroundImageSrc ? (
						<Image
							src={sectionBackgroundImageSrc}
							alt={sectionBackgroundImageAlt ?? ""}
							title={
								sectionBackgroundImageTitle ??
								sectionBackgroundImageAlt ??
								undefined
							}
							fill
							aria-hidden={sectionBackgroundImageAlt ? undefined : true}
							className={mediaObjectFitClassName(
								sectionBackgroundImageObjectFit,
							)}
						/>
					) : null}

					<div className="relative z-10 flex flex-col gap-8">
						{cards.length === 1 && cards[0] ? (
							<div
								className={cn(
									"w-full",
									sectionBackgroundImageSrc
										? "container-padding-x"
										: "max-sm:px-0 px-6",
								)}
							>
								{renderCard(cards[0], 0)}
							</div>
						) : (
							<>
								<div className="2xl:hidden">
									<Carousel
										opts={{
											align: "start",
											loop: false,
										}}
										className="w-full"
									>
										<CarouselContent
											className={cn(
												"items-stretch",
												!sectionBackgroundImageSrc ? "-ml-4" : "ml-0",
												mobileHasPeekCard ? "" : "",
											)}
										>
											{cards.map((card, index) => {
												return (
													<CarouselItem
														key={`${card.title ?? "card"}-${index}`}
														className={cn(
															"flex sm:first:pl-4",
															sectionBackgroundImageSrc ? "pl-5 last:pr-5" : "",
															mobileHasPeekCard ? "basis-9/10" : "basis-full",
															largeHasPeekCard
																? "lg:basis-[48%]"
																: "lg:basis-1/2",
														)}
													>
														{renderCard(card, index)}
													</CarouselItem>
												);
											})}
										</CarouselContent>
									</Carousel>
								</div>

								<div
									className={cn(
										"hidden 2xl:grid 2xl:grid-flow-col 2xl:auto-cols-fr 2xl:gap-4 2xl:px-6",
									)}
								>
									{cards.map((card, index) => renderCard(card, index))}
								</div>
							</>
						)}
					</div>
				</div>
			</div>
		</section>
	);
}
